import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    CircularProgress,
    Card,
    CardContent,
    TextField,
    MenuItem,
    Chip,
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Rating
} from '@mui/material';
import Chart from 'react-apexcharts';
import AdminOSLayout from '../../../components/layout/AdminOSLayout';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DescriptionIcon from '@mui/icons-material/Description';
import SchoolIcon from '@mui/icons-material/School';
import StarIcon from '@mui/icons-material/Star';

const SatisfactionPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [organizations, setOrganizations] = useState<any[]>([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (user?.role === 'super-admin') {
            fetchOrganizations();
        }
        fetchDashboardData();
    }, [selectedOrg]);

    const fetchOrganizations = async () => {
        try {
            const res = await api.get('/api/organizations');
            setOrganizations(res.data);
        } catch (err) {
            console.error('Error fetching organizations', err);
        }
    };

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const params: any = {};
            if (selectedOrg) params.organizationId = selectedOrg;

            const res = await api.get('/api/satisfaction/dashboard', { params });
            setData(res.data);
        } catch (err) {
            console.error('Error fetching satisfaction data', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <AdminOSLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                </Box>
            </AdminOSLayout>
        );
    }

    if (!data) return null;

    // Filter reviews
    const filteredReviews = data.reviews.filter((review: any) =>
        review.overallPedagogy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Chart Options
    const paceChartOptions = {
        labels: Object.keys(data.charts.trainingPace),
        colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
        legend: { position: 'bottom' as const },
        title: { text: 'Rythme de formation', align: 'center' as const }
    };
    const paceChartSeries = Object.values(data.charts.trainingPace) as number[];

    const progressionChartOptions = {
        labels: Object.keys(data.charts.pedagogicalProgression),
        colors: ['#10b981', '#ef4444', '#f59e0b'],
        legend: { position: 'bottom' as const },
        title: { text: 'Progression Pédagogique', align: 'center' as const }
    };
    const progressionChartSeries = Object.values(data.charts.pedagogicalProgression) as number[];

    const npsChartOptions = {
        chart: { type: 'bar' as const },
        xaxis: { categories: Object.keys(data.charts.recommendationDistribution) },
        colors: ['#3b82f6'],
        title: { text: 'Distribution NPS', align: 'center' as const }
    };
    const npsChartSeries = [{
        name: 'Avis',
        data: Object.values(data.charts.recommendationDistribution) as number[]
    }];

    return (
        <AdminOSLayout>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h4" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}>
                        Satisfaction Apprenants
                    </Typography>

                    {user?.role === 'super-admin' && (
                        <TextField
                            select
                            label="Filtrer par Organisme"
                            value={selectedOrg}
                            onChange={(e) => setSelectedOrg(e.target.value)}
                            sx={{ minWidth: 250 }}
                            size="small"
                        >
                            <MenuItem value="">Tous les organismes</MenuItem>
                            {organizations.map((org: any) => (
                                <MenuItem key={org._id} value={org._id}>{org.name}</MenuItem>
                            ))}
                        </TextField>
                    )}
                </Box>

                {/* KPI Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.main' }}>
                                    <StarIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{data.kpi.recommendation}/10</Typography>
                                    <Typography variant="body2" color="text.secondary">Recommandation Moyenne</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.light', color: 'success.main' }}>
                                    <DescriptionIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{data.kpi.documentation}/5</Typography>
                                    <Typography variant="body2" color="text.secondary">Qualité Documentation</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'info.light', color: 'info.main' }}>
                                    <SchoolIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{data.kpi.relevance}/5</Typography>
                                    <Typography variant="body2" color="text.secondary">Pertinence Cours</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'warning.light', color: 'warning.main' }}>
                                    <ThumbUpIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{data.kpi.count}</Typography>
                                    <Typography variant="body2" color="text.secondary">Avis Récoltés</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Charts */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                            <Chart options={paceChartOptions} series={paceChartSeries} type="donut" height={300} />
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                            <Chart options={progressionChartOptions} series={progressionChartSeries} type="pie" height={300} />
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                            <Chart options={npsChartOptions} series={npsChartSeries} type="bar" height={300} />
                        </Paper>
                    </Grid>
                </Grid>

                {/* Reviews List */}
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">Derniers Avis</Typography>
                        <TextField
                            placeholder="Rechercher dans les avis..."
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ width: 300 }}
                        />
                    </Box>
                    <Divider />
                    <List>
                        {filteredReviews.map((review: any) => (
                            <React.Fragment key={review._id}>
                                <ListItem alignItems="flex-start">
                                    <ListItemAvatar>
                                        <Avatar>{review.user.charAt(0)}</Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {review.user}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Rating value={review.rating} max={10} readOnly size="small" />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {new Date(review.date).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        }
                                        secondary={
                                            <Box component="span">
                                                <Typography variant="body2" color="primary" sx={{ my: 0.5 }}>
                                                    {review.category}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    "{review.overallPedagogy}"
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                                <Divider variant="inset" component="li" />
                            </React.Fragment>
                        ))}
                        {filteredReviews.length === 0 && (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography color="text.secondary">Aucun avis trouvé</Typography>
                            </Box>
                        )}
                    </List>
                </Paper>
            </Box>
        </AdminOSLayout>
    );
};

export default SatisfactionPage;
